import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';



const CustomDropdown = ({ label, options, selectedOption, onSelect }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    
  
    const handleToggle = () => {
      setIsOpen((prev) => !prev);
    };
  
    const handleOptionClick = (option) => {
      onSelect(option);
      setIsOpen(false);
    };
  
    // Close dropdown if user clicks outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
  
    return (
      <div ref={dropdownRef} style={{ position: 'relative', width: '850px', margin: '20px 20px -10px 20px' }}>
        <div
          onClick={handleToggle}
          style={{
            border: '2px solid #80ADF4',
            borderRadius: '8px',
            padding: '10px',
            fontSize: '16px',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#fff'
          }}
        >
          <span>{selectedOption || label}</span>
          <svg
            style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
            width="24"
            height="24"
            fill="gray"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M7 10l5 5 5-5z" />
          </svg>
        </div>
        {isOpen && (
          <ul
            style={{
              position: 'absolute',
              top: 'calc(100% + 5px)',
              left: 0,
              width: '100%',
              listStyleType: 'none',
              margin: 0,
              padding: 0,
              border: '1px solid #8A8A8A',
              borderRadius: '5px',
              backgroundColor: '#fff',
              boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
              zIndex: 999
            }}
          >
            {options.map((option, index) => (
              <li
                key={index}
                onClick={() => handleOptionClick(option)}
                style={{
                  padding: '10px',
                  cursor: 'pointer',
                  borderBottom: index !== options.length - 1 ? '1px solid #eee' : 'none',
                  backgroundColor: '#fff',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f6f6f6')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#fff')}
              >
                {option}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

const ToggleSwitch = ({ labelLeft, labelRight, checked, onChange }) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span 
          style={{
            fontSize: '16px',
            fontWeight: '500',
            color: '#333',
            marginRight: '10px',
            opacity: checked ? 0.5 : 1, // Dim the inactive label
            transition: 'opacity 0.3s',
          }}
        >
          {labelLeft}
        </span>
        <label 
          style={{ 
            position: 'relative', 
            display: 'inline-block', 
            width: '60px', 
            height: '34px', 
            margin: '0 10px',
          }}
        >
          <input 
            type="checkbox" 
            checked={checked} 
            onChange={onChange} 
            style={{ opacity: 0, width: 0, height: 0 }} 
          />
          <span 
            style={{
              position: 'absolute',
              cursor: 'pointer',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: checked ? '#4CAF50' : '#ccc',
              transition: '.4s',
              borderRadius: '34px',
            }}
          ></span>
          <span 
            style={{
              position: 'absolute',
              content: '""',
              height: '26px',
              width: '26px',
              left: checked ? '30px' : '4px',
              bottom: '4px',
              backgroundColor: 'white',
              transition: '.4s',
              borderRadius: '50%',
            }}
          ></span>
        </label>
        <span 
          style={{
            fontSize: '16px',
            fontWeight: '500',
            color: '#333',
            marginLeft: '10px',
            opacity: checked ? 1 : 0.5, // Dim the inactive label
            transition: 'opacity 0.3s',
          }}
        >
          {labelRight}
        </span>
      </div>
    );
  };

function UploadFramework() {
  const [isManual, setIsManual] = useState(false);
  const [predefined, setPredefined] = useState(false);
  const location = useLocation();
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState(null);
  const [isOn, setIsOn] = useState(true);
  const [currentCourse, setCurrentCourse] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(location.state?.selectedCourse || '');
  const [frameworks, setFrameworks] = useState(location.state?.frameworks || []);
  const coursesList = location.state.coursesList || [];

  const navigate = useNavigate();

  const switchToManual = () => {
    setIsManual((prev) => !prev);
    navigate('/score', { state: { isManual: true } });
  };

  const createSampleExcelURL = () => {
    // Create a new workbook
    const workbook = XLSX.utils.book_new();
  
    // Header row
    const worksheetData = [
      ["Framework Name", "Keywords", "Concepts"]
    ];
  
    frameworks.forEach((fw) => {
      const fwName = fw.name || "";
      const kwArray = fw.keywords || [];
      const cptArray = fw.concepts || [];
  
      const kwString = Array.isArray(kwArray) ? kwArray.join('\n') : kwArray.toString();
      const cptString = Array.isArray(cptArray) ? cptArray.join('\n') : cptArray.toString();
  
      worksheetData.push([fwName, kwString, cptString]);
    });
  
    if (frameworks.length === 0) {
      worksheetData.push(["Sample Framework", "keyword1\nkeyword2", "concept1\nconcept2"]);
    }
  
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  
    // Apply styling to header cells
    // Make sure your XLSX version supports cell styling.
    const headerStyle = { font: { bold: true } };
    const headerCells = ['A1', 'B1', 'C1'];
    headerCells.forEach(cell => {
      if (worksheet[cell]) {
        worksheet[cell].s = headerStyle;
      }
    });
  
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample");
  
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    return URL.createObjectURL(blob);
  };

  const sampleExcelURL = createSampleExcelURL();

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handlePredefinedChange = (e) => {
    setPredefined(e.target.checked);
    navigate('/score');
  };

  const handleFile = (newFile) => {
    setFile(newFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  useEffect(() => {
    if (!isOn) {
      const timer = setTimeout(() => {
        navigate('/score', { state: { selectedCourse: selectedCourse } });
      }, 300); // Delay for 300ms to allow CSS to take effect
      return () => clearTimeout(timer); // Cleanup the timer
    }
  }, [isOn, navigate]);

  useEffect(() => {
    if (file) {
      const reader = new FileReader();
  
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
  
        // Convert the worksheet to JSON
        const rows = XLSX.utils.sheet_to_json(worksheet, { raw: false });
  
        const requiredColumns = ['Framework Name', 'Keywords', 'Concepts'];
        const parsedColumns = rows.length > 0 ? Object.keys(rows[0]) : [];
  
        // Check if all required columns are present
        const hasAllRequired = requiredColumns.every((col) => parsedColumns.includes(col));
        const hasExactColumns = parsedColumns.length === requiredColumns.length;
  
        if (!hasAllRequired || !hasExactColumns) {
          alert('Error: Excel file must contain exactly "Framework Name", "Keywords", and "Concepts" columns.');
          return;
        }
  
        // Filter out rows where 'Framework Name' starts with '#' or contains only comments
        const filteredRows = rows.filter((row) => {
          const frameworkName = row['Framework Name'];
          if (!frameworkName) return false;
          const lines = frameworkName.split(/\r?\n/).map((line) => line.trim());
          return lines.some((line) => line && !line.startsWith('#'));
        });
  
        const frameworks = filteredRows.map((row) => {
          const frameworkName = row['Framework Name']
            ? row['Framework Name']
                .split(/\r?\n/) // Split multiline cells
                .map((line) => line.trim()) // Trim whitespace
                .filter((line) => !line.startsWith('#')) // Ignore lines starting with '#'
                .join(' ') // Rejoin valid lines into a single string
            : '';
          const keywords = row['Keywords']
            ? row['Keywords']
                .split(/\r?\n/) // Split multiline cells
                .map((kw) => kw.trim())
                .filter((kw) => !kw.startsWith('#')) // Ignore lines starting with '#'
            : [];
          const concepts = row['Concepts']
            ? row['Concepts']
                .split(/\r?\n/) // Split multiline cells
                .map((cpt) => cpt.trim())
                .filter((cpt) => !cpt.startsWith('#')) // Ignore lines starting with '#'
            : [];
  
          return {
            name: frameworkName,
            keywords,
            concepts,
          };
        });
  
        localStorage.setItem('frameworks', JSON.stringify(frameworks));
        navigate('/score');
      };
  
      reader.onerror = (error) => {
        console.error('Error reading Excel file:', error);
      };
  
      reader.readAsArrayBuffer(file);
    }
  }, [file, navigate]);

  return (
    <div className="main-body">
      <div className="top-bar">
        <img className='logo' src="logo.svg" alt="logo" />
        <button className="manual-input" onClick={switchToManual}>
          {isManual ? 'Use Predefined Framework' : 'Switch to Manual Input'}
        </button>
      </div>

      <div className="content">
        <div className="title">Program Competency Mapper</div>

        <div className='back-to-predefined' style={{ textAlign: 'center', marginTop: '30px' }}>
            <ToggleSwitch 
              labelLeft="Predefined Framework" 
              labelRight="Upload a Framework" 
              checked={isOn} 
              onChange={(e) => setIsOn(e.target.checked)} 
            />
        </div>

        <div className="course-search-container" style={{ marginBottom: '20px' }}>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              paddingTop: '20px' 
            }}
          >
            <Card 
              sx={{
                width: '96%',
                marginLeft: "20px",
                marginRight: "20px",
                borderRadius: '10px',
                boxShadow: '0px 4px 16px rgba(0,0,0,0.1)',
                backgroundColor: '#FFFFFF'
              }}
            >
              <CardContent>
                {/* Upload Section */}
                {!isManual && (
                  <Box
                    sx={{ 
                      border: '2px dashed #C7C7C7', 
                      borderRadius: '10px', 
                      height: '150px', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      marginBottom: '20px',
                      backgroundColor: dragActive ? '#f3f2ff' : '#fff',
                      transition: 'background-color 0.3s'
                    }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    {!file && (
                      <>
                        <IconButton component="label">
                          <AddIcon sx={{ fontSize: '40px', color: '#7367F0' }} />
                          <input 
                            type="file" 
                            accept=".xlsx" 
                            hidden 
                            onChange={handleFileChange}
                          />
                        </IconButton>
                        <Typography variant="body1" sx={{ color: '#7A7A7A' }}>
                          Drag & Drop your file here or click to upload
                        </Typography>
                      </>
                    )}
                    {file && (
                      <Typography variant="body1" sx={{ color: '#7A7A7A' }}>
                        {file.name}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Download Sample CSV Section */}
                {!isManual && (
                  <>
                    <Typography
                      sx={{
                        textAlign: 'center',
                        color: '#7A7A7A',
                        marginBottom: '10px',
                        marginTop: '100px',
                        fontSize: '20px'
                      }}
                    >
                      Please download the sample Excel file to see the required format.
                    </Typography>
                    <Box sx={{ textAlign: 'center', marginBottom: '20px' }}>
                      <Button
                        variant="contained"
                        href={sampleExcelURL}
                        download="aristai_program_competency_mapper_sample.xlsx"
                        sx={{
                          borderRadius: '6px',
                          textTransform: 'none',
                          backgroundColor: '#7367F0',
                          color: '#ffffff',
                          '&:hover': {
                            backgroundColor: '#5a54c7',
                          },
                        }}
                      >
                        Download Sample Excel
                      </Button>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        </div>

        

        <div className='button-white'></div>
      </div>
    </div>
  );
}

export default UploadFramework;
